import { tv } from "tailwind-variants";

/**
 * Compute up to two initials from a display name.
 *
 * Splits on whitespace, takes the first character of the first two words,
 * and uppercases them. Returns an empty string when the input is blank.
 */
export function getInitials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? "")
    .join("");
}

const avatarStyles = tv({
  base: "inline-flex shrink-0 items-center justify-center rounded-full",
  variants: {
    size: {
      xs: "size-4",
      sm: "size-5",
      md: "size-6",
      lg: "size-10",
    },
  },
  defaultVariants: {
    size: "sm",
  },
});

const avatarImageStyles = tv({
  base: "rounded-full object-cover",
  variants: {
    size: {
      xs: "size-4",
      sm: "size-5",
      md: "size-6",
      lg: "size-10",
    },
  },
  defaultVariants: {
    size: "sm",
  },
});

const avatarFallbackStyles = tv({
  extend: avatarStyles,
  base: "bg-muted font-semibold text-muted-fg",
  variants: {
    size: {
      xs: "text-[7px]",
      sm: "text-[9px]",
      md: "text-[10px]",
      lg: "text-xs",
    },
  },
});

export type AvatarSize = "xs" | "sm" | "md" | "lg";

export interface AvatarProps {
  /** URL of the avatar image. Falls back to initials when absent. */
  avatarUrl?: string | null;
  /** Display name used for alt text and initials fallback. */
  name: string;
  /** Size variant. */
  size?: AvatarSize;
  /** Additional CSS classes applied to the outermost element. */
  className?: string;
}

export function Avatar({ avatarUrl, name, size = "sm", className }: AvatarProps) {
  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={name}
        className={avatarImageStyles({ size, className })}
      />
    );
  }

  return (
    <span className={avatarFallbackStyles({ size, className })}>{getInitials(name)}</span>
  );
}
